import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProjectFilter } from "@/context/ProjectFilterContext";
import { useAuth } from "@/context/AuthContext";
import { ProjectMultiSelect } from "@/components/filters/ProjectMultiSelect";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { DataSourceLegend } from "@/components/DataSourceBadge";
import { RotateCcw, Plus, CalendarRange, Check } from "lucide-react";
import { toast } from "sonner";

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const HALVES = [
  { value: "H1", label: "H1 · Apr–Sep" },
  { value: "H2", label: "H2 · Oct–Mar" },
];
const FY_MONTHS = ["Apr-26", "May-26", "Jun-26", "Jul-26", "Aug-26", "Sep-26", "Oct-26", "Nov-26", "Dec-26", "Jan-27", "Feb-27", "Mar-27"];
const MONTH_RE = /^[A-Za-z]{3}-\d{2}$/;

/**
 * Sticky shared filter bar (project multi-select + Month/Quarter/Year period)
 * used by Dashboard + Analytics. Reads/writes global ProjectFilter state.
 */
export function AnalyticsFilterBar({ activeMonths = [], showLegend = false, rightSlot = null, filtersConfig = null, allowedProjects = null, corporateProjects = [], multiMonth = false, embedded = false, testId = "analytics-filter-bar" }) {
  const {
    selected, setSelected, gran, setGran, quarter, setQuarter, half, setHalf, month, setMonth, months, setMonths, remember, setRemember,
  } = useProjectFilter();
  const { isFull } = useAuth();
  const [meta, setMeta] = useState({ projects: [], regions: [], categories: [], teams: [], special: [] });
  const [customMonths, setCustomMonths] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newMonth, setNewMonth] = useState("");

  const corpKey = (corporateProjects || []).join(",");
  useEffect(() => {
    api.lists().then((l) => {
      // Sumadhura / Signet / Technology are NOT selectable "projects" — they surface
      // as their own spend groups (Office / Digital Support) instead. Real
      // project categories therefore reduce to Residential + Commercial only.
      // NOTE (PART 3 3.8): the legacy special "Brand (Sumadhura)" chip is removed —
      // corporate/brand spend is now reached via the "Corporate Spends" toggle in
      // Budget & Spend Health, not through the project selector.
      const SPECIAL_DEFS = [
        { name: "Signet", label: "Office", desc: "Signet — office / commercial-operations spend" },
        { name: "Technology", label: "Digital Support", desc: "Technology — shared digital-support spend" },
      ];
      const specialNames = Array.from(new Set([
        ...(corporateProjects || []), "Sumadhura", ...SPECIAL_DEFS.map((s) => s.name), "All Projects", "Tech",
      ]));
      const corpSet = new Set(specialNames);
      let projects = (l.projects || [])
        .filter((p) => !corpSet.has(p.name))
        .map((p) => ({ name: p.name, region: p.region, category: p.category, team: p.team }));
      if (allowedProjects && allowedProjects.length) {
        const allow = new Set(allowedProjects);
        projects = projects.filter((p) => allow.has(p.name));
      }
      const regions = Array.from(new Set(projects.map((p) => p.region).filter(Boolean)));
      const categories = Array.from(new Set(projects.map((p) => p.category).filter(Boolean)));
      const teams = Array.from(new Set(projects.map((p) => p.team).filter(Boolean)));
      const existing = new Set((l.projects || []).map((p) => p.name));
      const special = SPECIAL_DEFS.filter((s) => existing.has(s.name));
      setMeta({ projects, regions, categories, teams, special });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedProjects ? allowedProjects.join(",") : "", corpKey]);

  useEffect(() => { api.customMonths().then((d) => setCustomMonths(d.months || [])).catch(() => {}); }, []);

  const monthOptions = [...FY_MONTHS, ...customMonths.filter((m) => !FY_MONTHS.includes(m))];
  const reset = () => { setSelected([]); setGran("Yearly"); setMonths([]); };

  // multi-month helpers
  const selMonths = months && months.length ? months : (month ? [month] : []);
  const toggleMonth = (m) => {
    const set = new Set(selMonths);
    if (set.has(m)) set.delete(m); else set.add(m);
    const next = monthOptions.filter((x) => set.has(x)); // keep canonical order
    if (next.length) { setMonths(next); setMonth(next[0]); }
    else { setMonths([]); }
  };
  const monthLabel = selMonths.length === 0 ? "Select months"
    : selMonths.length === 1 ? selMonths[0]
      : `${selMonths.length} months`;

  const configuredGrans = (filtersConfig && Array.isArray(filtersConfig.granularities) && filtersConfig.granularities.length)
    ? filtersConfig.granularities : ["Monthly", "Quarterly", "Yearly"];
  // Ensure Half-yearly is always offered (Phase O), inserted right after Quarterly.
  const grans = configuredGrans.includes("Half-yearly")
    ? configuredGrans
    : configuredGrans.includes("Quarterly")
      ? configuredGrans.flatMap((g) => (g === "Quarterly" ? ["Quarterly", "Half-yearly"] : [g]))
      : [...configuredGrans, "Half-yearly"];
  const showRegion = false;  // Region dropped (Bengaluru-only focus)
  const showCategory = !filtersConfig || filtersConfig.show_category !== false;
  const showTeam = !!(filtersConfig && filtersConfig.show_team);
  const GRAN_LABELS = { Monthly: "Month", Quarterly: "Quarter", "Half-yearly": "Half-Year", Yearly: "Year" };

  const addMonth = async () => {
    const m = (newMonth || "").trim();
    if (!MONTH_RE.test(m)) { toast.error("Use the format Mon-YY (e.g., Apr-27)"); return; }
    if (monthOptions.includes(m)) { toast.error("That month already exists"); return; }
    try {
      const res = await api.saveCustomMonths([...customMonths, m]);
      setCustomMonths(res.months || []);
      setAddOpen(false); setNewMonth("");
      setGran("Monthly"); setMonth(m); setMonths(multiMonth ? [m] : []);
      toast.success(`Added ${m}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add month"); }
  };

  return (
    <div
      data-testid={testId}
      className={embedded
        ? ""
        : "sticky top-16 z-20 -mx-4 mb-1 border-b bg-background/85 px-4 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-background/60 sm:-mx-6 sm:px-6"}
    >
      <div className={embedded ? "flex flex-wrap items-center justify-end gap-2" : "flex flex-wrap items-center gap-2"}>
        <ProjectMultiSelect
          projects={meta.projects} regions={meta.regions} categories={meta.categories} teams={meta.teams}
          special={meta.special}
          showRegion={showRegion} showCategory={showCategory} showTeam={showTeam}
          selected={selected} onChange={setSelected}
          remember={remember} onRememberChange={setRemember}
        />

        <ToggleGroup type="single" value={gran} onValueChange={(v) => v && setGran(v)}
          className="rounded-lg border bg-card p-0.5" data-testid="analytics-period-granularity-toggle">
          {grans.map((g) => (
            <ToggleGroupItem key={g} value={g} className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid={`period-gran-${g.toLowerCase()}`}>{GRAN_LABELS[g] || g}</ToggleGroupItem>
          ))}
        </ToggleGroup>

        {gran === "Quarterly" && (
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="h-9 w-[110px]" data-testid="analytics-period-value-select"><SelectValue /></SelectTrigger>
            <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q} data-testid={`period-option-${q}`}>{q}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {gran === "Half-yearly" && (
          <Select value={half} onValueChange={setHalf}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="analytics-period-half-select"><SelectValue /></SelectTrigger>
            <SelectContent>{HALVES.map((h) => <SelectItem key={h.value} value={h.value} data-testid={`period-option-${h.value}`}>{h.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {gran === "Monthly" && (
          <div className="flex items-center gap-1.5">
            {multiMonth ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-9 min-w-[150px] justify-start gap-1.5" data-testid="analytics-month-multiselect-trigger">
                    <CalendarRange className="h-4 w-4 text-muted-foreground" /> {monthLabel}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start" data-testid="analytics-month-multiselect-popover">
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className="text-[11px] font-medium text-muted-foreground">Pick one or more months</span>
                    <button className="text-[11px] text-[hsl(var(--primary))] hover:underline" onClick={() => setMonths([])} data-testid="month-multiselect-clear">Clear</button>
                  </div>
                  <div className="max-h-[280px] space-y-0.5 overflow-y-auto">
                    {monthOptions.map((m) => {
                      const on = selMonths.includes(m);
                      return (
                        <label key={m} htmlFor={`mm-${m}`} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-secondary" data-testid={`month-multiselect-option-${m}`}>
                          <Checkbox id={`mm-${m}`} checked={on} onCheckedChange={() => toggleMonth(m)} />
                          <span>{m}</span>
                          {on && <Check className="ml-auto h-3.5 w-3.5 text-[hsl(var(--primary))]" />}
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <Select value={month} onValueChange={(v) => { setMonth(v); setMonths([]); }}>
                <SelectTrigger className="h-9 w-[128px]" data-testid="analytics-period-value-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[320px]">{monthOptions.map((m) => <SelectItem key={m} value={m} data-testid={`period-option-${m}`}>{m}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {isFull && (
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setAddOpen(true)} title="Add a month" data-testid="add-month-button">
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {showLegend && <DataSourceLegend />}
          {rightSlot}
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={reset} data-testid="analytics-reset-filters">
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
          </Button>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-month-dialog">
          <DialogHeader>
            <DialogTitle>Add a month</DialogTitle>
            <DialogDescription>Add a custom month to the period toggle (format Mon-YY, e.g., Apr-27).</DialogDescription>
          </DialogHeader>
          <Input value={newMonth} onChange={(e) => setNewMonth(e.target.value)} placeholder="Apr-27"
            onKeyDown={(e) => { if (e.key === "Enter") addMonth(); }} data-testid="add-month-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="add-month-cancel">Cancel</Button>
            <Button onClick={addMonth} data-testid="add-month-confirm">Add month</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
